import { baseFetch } from "./base";
import type {
  AsyncRoleplaySession,
  AsyncRoleplaySessionsList,
} from "../types/asyncRoleplay";

type AxiosLikeError = {
  response?: {
    status?: number;
    data?: { detail?: unknown };
  };
  message?: string;
};

const detailFromError = (error: unknown): string | null => {
  const axiosError = error as AxiosLikeError;
  const detail = axiosError?.response?.data?.detail;
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  try {
    return JSON.stringify(detail);
  } catch {
    return null;
  }
};

export const getAsyncRoleplayErrorDetail = (error: unknown): string => {
  const axiosError = error as AxiosLikeError;
  const status = axiosError?.response?.status;
  const detail = detailFromError(error);

  switch (status) {
    case 401:
      return detail || "Authentication required. Please sign in again.";
    case 404:
      return detail || "Session not found or belongs to another user.";
    case 409:
      return (
        detail ||
        "Session is busy, finished, pending a turn, out of speech budget, or domain/focus does not match."
      );
    case 413:
      return detail || "Upload is too large (max 20 MiB).";
    case 415:
      return (
        detail ||
        "Unsupported audio format. Use wav, mp3, ogg, webm, m4a, or mp4."
      );
    case 422:
      return (
        detail ||
        "Invalid request (settings, audio, duration, or finish requirements)."
      );
    case 429:
      return detail || "Weekly roleplay limit exceeded.";
    default:
      break;
  }

  if (detail) return detail;
  if (error instanceof Error) return error.message;
  if (typeof axiosError?.message === "string") return axiosError.message;
  return "Request failed";
};

const extensionForBlob = (blob: Blob): string => {
  const type = (blob.type || "").toLowerCase();
  if (type.includes("mp4") || type.includes("m4a") || type.includes("aac")) {
    return "m4a";
  }
  if (type.includes("ogg")) return "ogg";
  if (type.includes("wav")) return "wav";
  if (type.includes("mp3") || type.includes("mpeg")) return "mp3";
  // Empty type on some iOS builds — prefer m4a over webm for Apple.
  const ua = navigator.userAgent.toLowerCase();
  if (
    ua.includes("iphone") ||
    ua.includes("ipad") ||
    ua.includes("ipod") ||
    (ua.includes("safari") && !ua.includes("chrome") && !ua.includes("android"))
  ) {
    return "m4a";
  }
  return "webm";
};

export const createAsyncRoleplaySession = async (
  settings: Record<string, unknown>,
): Promise<AsyncRoleplaySession> => {
  try {
    const response = await baseFetch.post<AsyncRoleplaySession>(
      "/async_roleplay/sessions",
      settings,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error creating async roleplay session:",
      getAsyncRoleplayErrorDetail(error),
    );
    throw error;
  }
};

export const listAsyncRoleplaySessions = async (): Promise<
  AsyncRoleplaySession[]
> => {
  try {
    const response =
      await baseFetch.get<AsyncRoleplaySessionsList>("/async_roleplay/sessions");
    return response.data?.sessions ?? [];
  } catch (error) {
    console.error(
      "Error listing async roleplay sessions:",
      getAsyncRoleplayErrorDetail(error),
    );
    throw error;
  }
};

export const getAsyncRoleplaySession = async (
  sessionId: string,
  options?: { signal?: AbortSignal },
): Promise<AsyncRoleplaySession> => {
  try {
    const response = await baseFetch.get<AsyncRoleplaySession>(
      `/async_roleplay/sessions/${sessionId}`,
      { signal: options?.signal },
    );
    return response.data;
  } catch (error) {
    if (!options?.signal?.aborted) {
      console.error(
        "Error fetching async roleplay session:",
        getAsyncRoleplayErrorDetail(error),
      );
    }
    throw error;
  }
};

export const sendAsyncRoleplayVoice = async (
  sessionId: string,
  messageId: string,
  blob: Blob,
): Promise<AsyncRoleplaySession> => {
  const form = new FormData();
  form.append("message_id", messageId);
  const extension = extensionForBlob(blob);
  form.append("audio", blob, `voice.${extension}`);

  try {
    const response = await baseFetch.post<AsyncRoleplaySession>(
      `/async_roleplay/sessions/${sessionId}/messages`,
      form,
      {
        headers: {
          // Override axios instance default JSON so the browser sets the boundary.
          "Content-Type": undefined,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error uploading async roleplay voice:",
      getAsyncRoleplayErrorDetail(error),
    );
    throw error;
  }
};

export const retryAsyncRoleplayMessage = async (
  sessionId: string,
  messageId: string,
): Promise<AsyncRoleplaySession> => {
  try {
    const response = await baseFetch.post<AsyncRoleplaySession>(
      `/async_roleplay/sessions/${sessionId}/messages/${messageId}/retry`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error retrying async roleplay message:",
      getAsyncRoleplayErrorDetail(error),
    );
    throw error;
  }
};

export const discardAsyncRoleplayMessage = async (
  sessionId: string,
  messageId: string,
): Promise<AsyncRoleplaySession> => {
  try {
    const response = await baseFetch.delete<AsyncRoleplaySession>(
      `/async_roleplay/sessions/${sessionId}/messages/${messageId}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error discarding async roleplay message:",
      getAsyncRoleplayErrorDetail(error),
    );
    throw error;
  }
};

export const finishAsyncRoleplaySession = async (
  sessionId: string,
): Promise<AsyncRoleplaySession> => {
  try {
    const response = await baseFetch.post<AsyncRoleplaySession>(
      `/async_roleplay/sessions/${sessionId}/finish`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error finishing async roleplay session:",
      getAsyncRoleplayErrorDetail(error),
    );
    throw error;
  }
};
